import { useEffect, useMemo, useState } from "react";
import { searchGlobalComps, assignCompsToDeal } from "../../supabase";
import { compKey } from "../../utils/compDedup";
import useMsaList from "../../hooks/useMsaList";

// ─── Comp Library Picker ────────────────────────────────────────────────────
// Modal that lets a user multi-select rows from the global comps library and
// assign them to a deal. Pre-filters to:
//   - 50mi of the deal's centroid (Haversine, client-side)
//   - Last 24 months of comp_date
//   - The active type (sale or lease)
//
// Already-assigned comps are visually flagged + their row checkbox is locked
// so the user can't re-add them. The "Add" action calls assignCompsToDeal()
// which is idempotent and silently skips duplicates anyway, but we prevent
// the user gesture for clarity.

function distMiles(lat1, lng1, lat2, lng2) {
  if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return Infinity;
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function fmtMoney(n) {
  if (n == null || n === 0) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)    return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}
function fmtSF(n) { return n ? `${Number(n).toLocaleString()} SF` : "—"; }
function fmtPct(n) { return n != null && n !== 0 ? `${Number(n).toFixed(2)}%` : "—"; }
function fmtRate(n) { return n != null && n !== 0 ? `$${Number(n).toFixed(2)}/SF` : "—"; }
function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-US", { year: "numeric", month: "short" });
}

export default function CompLibraryPicker({
  open,
  onClose,
  dealId,
  dealName,
  dealLat,
  dealLng,
  type,                           // 'sale' or 'lease'
  alreadyAssignedIds = new Set(), // ids already linked to this deal
  alreadyAssignedKeys = new Set(),// compKey-based, catches address-dupes across re-extractions
  onAssigned,                     // (insertedCount, insertedCompIds) => void
  darkMode = false,
}) {
  const [query, setQuery] = useState("");
  // MSA filter — pulls from msa_definitions; empty string = no filter (all MSAs).
  const [msaFilter, setMsaFilter] = useState("");
  const { msas: msaList } = useMsaList();
  const [sinceMonths, setSinceMonths] = useState(24);
  const [maxDistance, setMaxDistance] = useState(50);
  const [sourceFilter, setSourceFilter] = useState("all"); // 'all' | 'comp' | 'rent_roll' (lease only)
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);

  // Debounced fetch — re-query whenever filters change while the modal is open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      const rows = await searchGlobalComps({
        type,
        query,
        msa: msaFilter || null,
        sinceMonths: sinceMonths || null,
        limit: 500,
      });
      if (cancelled) return;
      setResults(rows);
      setLoading(false);
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, type, query, msaFilter, sinceMonths]);

  // Reset selection / filters whenever the modal opens fresh
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setQuery("");
      setSourceFilter("all");
    }
  }, [open]);

  const visibleRows = useMemo(() => {
    return results
      .filter(r => {
        if (sourceFilter === "comp" && r.source === "rent_roll") return false;
        if (sourceFilter === "rent_roll" && r.source !== "rent_roll") return false;
        if (maxDistance && dealLat && dealLng) {
          const d = distMiles(dealLat, dealLng, r.latitude, r.longitude);
          if (d > maxDistance) return false;
        }
        return true;
      })
      .map(r => ({
        ...r,
        _distance: (dealLat && dealLng) ? distMiles(dealLat, dealLng, r.latitude, r.longitude) : null,
        _alreadyAssigned: alreadyAssignedIds.has(r.id) || alreadyAssignedKeys.has(compKey(r)),
      }))
      .sort((a, b) => {
        if (a._alreadyAssigned !== b._alreadyAssigned) return a._alreadyAssigned ? 1 : -1;
        if (a._distance != null && b._distance != null) return a._distance - b._distance;
        return 0;
      });
  }, [results, sourceFilter, maxDistance, dealLat, dealLng, alreadyAssignedIds, alreadyAssignedKeys]);

  const selectableCount = visibleRows.filter(r => !r._alreadyAssigned).length;
  const allSelected = selectableCount > 0 && visibleRows.filter(r => !r._alreadyAssigned).every(r => selectedIds.has(r.id));

  function toggleOne(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      const next = new Set();
      visibleRows.forEach(r => { if (!r._alreadyAssigned) next.add(r.id); });
      setSelectedIds(next);
    }
  }

  async function handleAssign() {
    if (selectedIds.size === 0) return;
    setSubmitting(true);
    const ids = [...selectedIds];
    const result = await assignCompsToDeal(dealId, ids, "library_picker");
    setSubmitting(false);
    if (result.error) {
      window.alert(`Couldn't assign ${ids.length} comp(s): ${result.error}`);
      return;
    }
    onAssigned?.(result.count, ids);
    onClose?.();
  }

  if (!open) return null;

  const cls = darkMode
    ? { bg: "bg-[#242424]", panel: "bg-[#2c2c2c]", border: "border-[#3a3a3a]", text: "text-white", muted: "text-[#8a8a8a]", input: "bg-[#242424] border-[#3a3a3a] text-white placeholder-[#6a6a6a]" }
    : { bg: "bg-white",     panel: "bg-pp-light",  border: "border-pp-gray",  text: "text-pp-dark", muted: "text-[#8a8a8a]", input: "bg-white border-pp-gray text-pp-dark placeholder-[#7a7a7a]" };

  const typeLabel = type === "sale" ? "Sale Comps" : "Lease Comps";

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.55)" }} onClick={onClose}>
      <div className={`${cls.bg} ${cls.text} w-full max-w-5xl h-[85vh] rounded-xl shadow-2xl flex flex-col font-montserrat`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`px-5 py-4 border-b ${cls.border} flex items-center justify-between flex-shrink-0`}>
          <div>
            <h2 className="text-lg font-bold">Add {typeLabel} from Library</h2>
            <p className={`text-xs ${cls.muted} mt-0.5`}>{dealName ? `→ ${dealName}` : ""}</p>
          </div>
          <button onClick={onClose} className={`text-xl ${cls.muted} hover:${cls.text} px-2`} aria-label="Close">✕</button>
        </div>

        {/* Filters */}
        <div className={`px-5 py-3 border-b ${cls.border} flex flex-wrap gap-2 items-center flex-shrink-0`}>
          <input
            type="text"
            placeholder="Search address, tenant, buyer…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className={`flex-1 min-w-[200px] text-xs px-3 py-1.5 rounded-md border ${cls.input} focus:outline-none focus:border-pp-red`}
          />
          <select
            value={msaFilter}
            onChange={e => setMsaFilter(e.target.value)}
            className={`w-40 text-xs px-2 py-1.5 rounded-md border ${cls.input}`}
          >
            <option value="">All MSAs</option>
            {msaList.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={sinceMonths} onChange={e => setSinceMonths(Number(e.target.value))} className={`text-xs px-2 py-1.5 rounded-md border ${cls.input}`}>
            <option value={6}>Last 6 mo</option>
            <option value={12}>Last 12 mo</option>
            <option value={24}>Last 24 mo</option>
            <option value={60}>Last 5 yr</option>
            <option value={0}>All time</option>
          </select>
          <select value={maxDistance} onChange={e => setMaxDistance(Number(e.target.value))} className={`text-xs px-2 py-1.5 rounded-md border ${cls.input}`} disabled={!dealLat || !dealLng}>
            <option value={10}>Within 10 mi</option>
            <option value={25}>Within 25 mi</option>
            <option value={50}>Within 50 mi</option>
            <option value={100}>Within 100 mi</option>
            <option value={0}>Any distance</option>
          </select>
          {type === "lease" && (
            <div className={`flex border ${cls.border} rounded-md overflow-hidden text-xs`}>
              {[
                { v: "all", l: "All" },
                { v: "comp", l: "Lease Comps" },
                { v: "rent_roll", l: "Rent Roll" },
              ].map(o => (
                <button key={o.v} onClick={() => setSourceFilter(o.v)}
                  className={`px-2 py-1.5 font-semibold ${sourceFilter === o.v ? "bg-pp-red text-white" : `${cls.muted} hover:${cls.text}`}`}
                  style={sourceFilter === o.v ? { color: "#ffffff" } : {}}>
                  {o.l}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Results header (select-all + count) */}
        <div className={`px-5 py-2 border-b ${cls.border} flex items-center gap-3 text-xs flex-shrink-0`}>
          <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-pp-red" disabled={selectableCount === 0} />
          <span className={cls.muted}>
            {loading ? "Searching…" : `${visibleRows.length} comp${visibleRows.length === 1 ? "" : "s"} match · ${selectedIds.size} selected`}
          </span>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {!loading && visibleRows.length === 0 && (
            <div className={`p-10 text-center text-sm ${cls.muted}`}>No comps match these filters. Try broadening the date range or distance.</div>
          )}
          {visibleRows.map(r => {
            const checked = selectedIds.has(r.id);
            const isRentRoll = r.source === "rent_roll";
            return (
              <label key={r.id}
                className={`flex items-start gap-3 px-5 py-3 border-b ${cls.border} cursor-pointer ${r._alreadyAssigned ? "opacity-50" : ""}`}
                style={{ background: checked ? (darkMode ? "rgba(233,53,41,0.08)" : "rgba(233,53,41,0.05)") : undefined }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleOne(r.id)}
                  disabled={r._alreadyAssigned}
                  className="mt-1 accent-pp-red"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-semibold truncate">{r.property_name || r.address?.split(",")[0]}</span>
                    {isRentRoll && (
                      <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded" style={{ background: "rgba(180,180,180,0.15)", color: "#b4b4b4" }}>RENT ROLL</span>
                    )}
                    {!isRentRoll && type === "lease" && (
                      <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded" style={{ background: "rgba(180,180,180,0.10)", color: "#b4b4b4" }}>LEASE COMP</span>
                    )}
                    {type === "sale" && (
                      <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded" style={{ background: "rgba(200,146,47,0.10)", color: "#c8922f" }}>SALE COMP</span>
                    )}
                    {r._alreadyAssigned && (
                      <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-[rgba(79,157,118,0.16)] text-[#3a7e5c]">ALREADY ASSIGNED</span>
                    )}
                    {r._distance != null && (
                      <span className={`text-[10px] ${cls.muted}`}>{r._distance.toFixed(1)} mi</span>
                    )}
                  </div>
                  <div className={`text-xs ${cls.muted} mt-0.5 truncate`}>{r.address}</div>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs mt-1">
                    {r.market && <span className={cls.muted}>{r.market}</span>}
                    {type === "sale" && (
                      <>
                        {r.sale_price && <span>{fmtMoney(r.sale_price)}</span>}
                        {r.building_sf && <span>{fmtSF(r.building_sf)}</span>}
                        {r.price_psf && <span>${Number(r.price_psf).toFixed(2)}/SF</span>}
                        {r.cap_rate && <span>{fmtPct(r.cap_rate)} cap</span>}
                      </>
                    )}
                    {type === "lease" && (
                      <>
                        {r.tenant && <span className="font-medium">{r.tenant}</span>}
                        {r.suite_sf && <span>{fmtSF(r.suite_sf)}</span>}
                        {r.lease_rate && <span>{fmtRate(r.lease_rate)}</span>}
                        {r.lease_type && <span className={cls.muted}>{r.lease_type}</span>}
                      </>
                    )}
                    {r.comp_date && <span className={cls.muted}>{fmtDate(r.comp_date)}</span>}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        {/* Footer */}
        <div className={`px-5 py-3 border-t ${cls.border} flex items-center justify-between flex-shrink-0`}>
          <span className={`text-xs ${cls.muted}`}>{selectedIds.size > 0 ? `${selectedIds.size} ready to assign` : "Pick one or more comps to add"}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className={`text-xs px-4 py-2 rounded-lg border ${cls.border} ${cls.muted} hover:${cls.text}`}>Cancel</button>
            <button
              onClick={handleAssign}
              disabled={selectedIds.size === 0 || submitting}
              style={{ color: "#ffffff" }}
              className="text-xs px-4 py-2 rounded-lg bg-pp-red hover:bg-pp-red-light font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? "Adding…" : `Add ${selectedIds.size} to Deal`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
